import { useEffect, useState, useRef, useCallback } from 'react'
import type { Position } from '../types'
import { haversineMeters, bearingDeg, now } from '../utils/geo'

export interface GeolocationState {
  position: Position | null
  error: GeolocationPositionError | null
  accuracy: number | null
  refresh: () => void // Manual refresh function
}

const POLL_INTERVAL_MS = 10000 // Poll every 10 seconds (for initial tracking)
const AUTO_UPDATE_INTERVAL_MS = 120000 // Auto-update every 2 minutes

export function useGeolocation(
  enabled: boolean,
  onPositionUpdate?: (position: Position, accuracy: number) => void,
  autoUpdate: boolean = true
): GeolocationState {
  const [position, setPosition] = useState<Position | null>(null)
  const [error, setError] = useState<GeolocationPositionError | null>(null)
  const [accuracy, setAccuracy] = useState<number | null>(null)
  const prevPosRef = useRef<Position | null>(null)
  const pollIntervalRef = useRef<number | null>(null)
  const autoUpdateIntervalRef = useRef<number | null>(null)
  const isInitialPositionRef = useRef(true)
  const onPositionUpdateRef = useRef(onPositionUpdate)
  
  // Keep callback ref up to date
  useEffect(() => {
    onPositionUpdateRef.current = onPositionUpdate
  }, [onPositionUpdate])

  useEffect(() => {
    if (!enabled) {
      // Clear polling when disabled
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }
      if (autoUpdateIntervalRef.current) {
        clearInterval(autoUpdateIntervalRef.current)
        autoUpdateIntervalRef.current = null
      }
      setPosition(null)
      setError(null)
      setAccuracy(null)
      prevPosRef.current = null
      isInitialPositionRef.current = true
      return
    }

    if (!navigator.geolocation) {
      setError({
        code: 0,
        message: 'Geolocation not supported',
        PERMISSION_DENIED: 1,
        POSITION_UNAVAILABLE: 2,
        TIMEOUT: 3,
      } as GeolocationPositionError)
      return
    }

    const handlePosition = (pos: GeolocationPosition) => {
      const lat = pos.coords.latitude
      const lon = pos.coords.longitude
      const acc = pos.coords.accuracy

      setAccuracy(acc)

      let heading: number | null = null
      const deviceHeading = pos.coords.heading

      if (
        typeof deviceHeading === 'number' &&
        !Number.isNaN(deviceHeading)
      ) {
        heading = (deviceHeading + 360) % 360
      } else if (prevPosRef.current) {
        const d = haversineMeters(
          prevPosRef.current.lat,
          prevPosRef.current.lon,
          lat,
          lon
        )
        if (d >= 10) {
          heading = bearingDeg(
            prevPosRef.current.lat,
            prevPosRef.current.lon,
            lat,
            lon
          )
        }
      }

      const newPosition: Position = {
        lat,
        lon,
        t: now(),
        heading,
      }

      setPosition(newPosition)
      prevPosRef.current = { lat, lon, t: now(), heading }
      // Use ref to avoid dependency issues
      onPositionUpdateRef.current?.(newPosition, acc)

      // If this is the initial position, mark it as done
      if (isInitialPositionRef.current) {
        isInitialPositionRef.current = false
      }
    }

    const handleError = (err: GeolocationPositionError) => {
      setError(err)
    }

    const geoOptions: PositionOptions = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 5000,
    }

    // Get initial position immediately
    navigator.geolocation.getCurrentPosition(
      handlePosition,
      handleError,
      geoOptions
    )

    // Poll frequently for initial tracking (first few updates)
    // After initial position, switch to less frequent updates if autoUpdate is enabled
    pollIntervalRef.current = window.setInterval(() => {
      navigator.geolocation.getCurrentPosition(
        handlePosition,
        handleError,
        geoOptions
      )
    }, POLL_INTERVAL_MS)
    
    // Auto-update every 2 minutes if enabled (only after initial position)
    if (autoUpdate) {
      autoUpdateIntervalRef.current = window.setInterval(() => {
        if (!isInitialPositionRef.current) {
          navigator.geolocation.getCurrentPosition(
            handlePosition,
            handleError,
            { ...geoOptions, maximumAge: 60000 } // Allow 1 minute old cache for auto-updates
          )
        }
      }, AUTO_UPDATE_INTERVAL_MS)
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
        pollIntervalRef.current = null
      }
      if (autoUpdateIntervalRef.current) {
        clearInterval(autoUpdateIntervalRef.current)
        autoUpdateIntervalRef.current = null
      }
    }
  }, [enabled, autoUpdate])

  // Manual refresh function
  const refreshPosition = useCallback(() => {
    if (!navigator.geolocation || !enabled) return
    
    const geoOptions: PositionOptions = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0, // Force fresh position
    }
    
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude
        const lon = pos.coords.longitude
        const acc = pos.coords.accuracy

        setAccuracy(acc)

        let heading: number | null = null
        const deviceHeading = pos.coords.heading

        if (
          typeof deviceHeading === 'number' &&
          !Number.isNaN(deviceHeading)
        ) {
          heading = (deviceHeading + 360) % 360
        } else if (prevPosRef.current) {
          const d = haversineMeters(
            prevPosRef.current.lat,
            prevPosRef.current.lon,
            lat,
            lon
          )
          if (d >= 10) {
            heading = bearingDeg(
              prevPosRef.current.lat,
              prevPosRef.current.lon,
              lat,
              lon
            )
          }
        }

        const newPosition: Position = {
          lat,
          lon,
          t: now(),
          heading,
        }

        setPosition(newPosition)
        prevPosRef.current = { lat, lon, t: now(), heading }
        onPositionUpdateRef.current?.(newPosition, acc)
      },
      (err) => setError(err),
      geoOptions
    )
  }, [enabled])

  return { position, error, accuracy, refresh: refreshPosition }
}
